import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import * as utils from '../utils/utils.js';

// Single shared socket instance
let sharedSocket = null;
let connectionCount = 0;
let isConnecting = false;
let disconnectTimeout = null;

export const useSocket = (eventId = null, sessionId = null) => {
    const isInitialized = useRef(false);
    const hasJoinedRoom = useRef(false);

    // Effect for socket initialization
    useEffect(() => {
        // Cancel any pending disconnection first
        if (disconnectTimeout) {
            clearTimeout(disconnectTimeout);
            disconnectTimeout = null;
        }

        if (!isInitialized.current && !isConnecting) {
            connectionCount++;
            isConnecting = true;

            // Create socket only if it doesn't exist
            if (!sharedSocket) {
                sharedSocket = io(utils.backendUrl(), {
                    withCredentials: true,
                    transports: ['polling', 'websocket'], // Try polling first, then websocket
                    timeout: 10000,
                    forceNew: false,
                    reconnection: true, // Enable reconnection but with limits
                    reconnectionAttempts: 3,
                    reconnectionDelay: 2000,
                    autoConnect: true
                });

                // Add connection event listeners for debugging
                sharedSocket.on('connect', () => {
                    isConnecting = false;

                    // Join event room if eventId is provided
                    if (eventId && !hasJoinedRoom.current) {
                        sharedSocket.emit('join-event', {
                            eventId,
                            sessionId,
                            participantId: null // Will be set by individual components if needed
                        });
                        hasJoinedRoom.current = true;
                    } else {
                        // console.log('Not joining room:', { eventId, hasJoined: hasJoinedRoom.current });
                    }
                });

              /*   sharedSocket.on('event-joined', (data) => {
                }); */

                sharedSocket.on('disconnect', (reason) => {
                    isConnecting = false;
                    hasJoinedRoom.current = false;
                });

                sharedSocket.on('connect_error', (error) => {
                    isConnecting = false;
                });

                // Add listener for attendance updates
                /* sharedSocket.on('attendance-changed', (data) => {
                }); */
            } else {
                isConnecting = false;

                // Join event room if eventId is provided and not already joined
                if (eventId && sharedSocket.connected && !hasJoinedRoom.current) {
                    sharedSocket.emit('join-event', {
                        eventId,
                        sessionId,
                        participantId: null
                    });
                    hasJoinedRoom.current = true;
                } else {
                    // console.log('Not joining room:', { eventId, connected: sharedSocket.connected, hasJoined: hasJoinedRoom.current });
                }
            }

            isInitialized.current = true;
        }

        return () => {
            if (isInitialized.current) {
                connectionCount--;

                // Clear any existing disconnect timeout
                if (disconnectTimeout) {
                    clearTimeout(disconnectTimeout);
                    disconnectTimeout = null;
                }

                // Disconnect only when no components are using it, with a small delay
                if (connectionCount <= 0 && sharedSocket) {
                    disconnectTimeout = setTimeout(() => {
                        if (connectionCount <= 0 && sharedSocket && !isConnecting) {
                            sharedSocket.disconnect();
                            sharedSocket = null;
                        } else {
                        }
                        disconnectTimeout = null;
                    }, 500);
                }
            }
        };
    }, [eventId, sessionId]);

    // Separate effect for joining rooms when eventId/sessionId become available
    useEffect(() => {
        if (sharedSocket && sharedSocket.connected && eventId && sessionId && !hasJoinedRoom.current) {
            sharedSocket.emit('join-event', {
                eventId,
                sessionId,
                participantId: null
            });
            hasJoinedRoom.current = true;
        }
    }, [eventId, sessionId]);

    const emitAttendanceUpdate = (data) => {
        if (sharedSocket?.connected) {
            sharedSocket.emit('attendance-update', data);
        }
    };

    const onAttendanceChanged = (callback) => {
        if (sharedSocket) {
            sharedSocket.on('attendance-changed', callback);
            return () => sharedSocket?.off('attendance-changed', callback);
        }
        return () => {};
    };

    const isConnected = () => {
        return sharedSocket?.connected || false;
    };

    // Return the socket instance directly for useChat hook compatibility
    // Also provide helper methods for attendance functionality
    if (sharedSocket) {
        sharedSocket.emitAttendanceUpdate = emitAttendanceUpdate;
        sharedSocket.onAttendanceChanged = onAttendanceChanged;
        sharedSocket.isConnected = isConnected;
    }

    return sharedSocket;
};
    